import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import {
	addCCAOfficeObservationCompetitor,
	deleteCCAOfficeObservationCompetitor,
	updateCCAOfficeObservationCompetitor,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { OfficeObservationCompetitor } from '@/libs/fleet-api/services/types';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReportCompetitorForm = {
	input?: OfficeObservationCompetitor;
	lists: OfficeObservationCompetitor[];
};

const CCAVisitReportCompetitor: FC = () => {
	const { officeObservation, surveyInformation } =
		useCCAVisitReportVerificationPage();
	const { marketingAspect } = officeObservation ?? {};
	const { form, fieldName } = useForm<VisitReportCompetitorForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addCCAOfficeObservationCompetitorMutation = useMutation({
		mutationFn: addCCAOfficeObservationCompetitor,
	});

	const updateCCAOfficeObservationCompetitorMutation = useMutation({
		mutationFn: updateCCAOfficeObservationCompetitor,
	});

	const deleteCCAOfficeObservationCompetitorMutation = useMutation({
		mutationFn: deleteCCAOfficeObservationCompetitor,
	});

	const { modal: modalConfirm } = useModals();

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists: marketingAspect?.competitors ?? [],
					} as VisitReportCompetitorForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateCCAOfficeObservationCompetitorMutation.mutateAsync({
							id: input!.id,
							name: input!.name,
							surveyId: surveyInformation!.id!,
						});
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res =
							await addCCAOfficeObservationCompetitorMutation.mutateAsync({
								name: input!.name,
								surveyId: surveyInformation!.id!,
							});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data.id }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportCompetitorForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Typography.Title level={3}>Kompetitor</Typography.Title>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addCCAOfficeObservationCompetitorMutation.isPending ||
											updateCCAOfficeObservationCompetitorMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalConfirm.confirm({
																			title:
																				'Anda yakin akan menghapus data ini?',
																		});

																	if (confirmDelete) {
																		await deleteCCAOfficeObservationCompetitorMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Kompetitor',
											render: (_, record) => record.name ?? '-',
										},
									]}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Kompetitor</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'name'])}
												label="Kompetitor"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
			<Divider />
		</Form.Provider>
	);
};

export default CCAVisitReportCompetitor;
